import {
  DownloadSimpleIcon,
  GiftIcon,
  MagicWandIcon,
  PlusIcon,
  StarIcon,
  ArrowsClockwise,
  type Icon,
} from "@phosphor-icons/react";
import type { CreditExpiry } from "@reactive-resume/dto";
import { Tooltip } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { Link, useLocation } from "react-router";

import { UserAvatar } from "@/client/components/user-avatar";
import { UserOptions } from "@/client/components/user-options";
import { describeCreditExpiry } from "@/client/services/credit/expiry";
import { useAccountCurrency } from "@/client/services/currency/currency";
import { useUser } from "@/client/services/user";
import { useDialog } from "@/client/stores/dialog";
import { useTopbarHighlight } from "@/client/stores/topbar-highlight";

import { USER_SPACE_PATHS } from "../../config/navigation";
import { NotificationBell } from "../notifications/notification-bell";

type MetricKey = "downloads" | "ai" | "rewards" | "fidelity";

type UserSpaceTopbarProps = {
  planName?: string;
  downloads?: number;
  downloadsExpiring?: CreditExpiry[];
  aiCredits?: number;
  rewards?: number;
  fidelity?: number;
  loading?: boolean;
  refreshing?: boolean;
  onRefresh?: () => void;
};

type TopbarMetric = {
  key: MetricKey;
  label: string;
  mobileLabel: string;
  path: string;
  icon: Icon;
  description: string;
  valueType: "currency" | "points" | "number";
  suffix?: string;
  iconClassName: string;
  iconBackgroundClassName: string;
  iconRingClassName: string;
  tooltipClassName: string;
};

const topbarMetrics: TopbarMetric[] = [
  {
    key: "downloads",
    label: "Téléchargements",
    mobileLabel: "Téléchargements",
    path: USER_SPACE_PATHS.resumes,
    icon: DownloadSimpleIcon,
    description: "Nombre de téléchargements de documents encore disponibles.",
    valueType: "number",
    suffix: "dispo.",
    iconClassName: "text-indigo-700",
    iconBackgroundClassName: "bg-indigo-50",
    iconRingClassName: "ring-indigo-100",
    tooltipClassName: "border-indigo-200 bg-indigo-100 text-indigo-950 before:bg-indigo-100",
  },
  {
    key: "ai",
    label: "Crédits IA",
    mobileLabel: "Crédits IA",
    path: USER_SPACE_PATHS.aiCredits,
    icon: MagicWandIcon,
    description:
      "Points disponibles pour utiliser les fonctionnalités d’intelligence artificielle.",
    valueType: "number",
    suffix: "points",
    iconClassName: "text-violet-700",
    iconBackgroundClassName: "bg-violet-50",
    iconRingClassName: "ring-violet-100",
    tooltipClassName: "border-violet-200 bg-violet-100 text-violet-950 before:bg-violet-100",
  },
  {
    key: "rewards",
    label: "Récompenses",
    mobileLabel: "Récompenses",
    path: USER_SPACE_PATHS.referral,
    icon: GiftIcon,
    description: "Gains obtenus grâce au parrainage et au programme ambassadeur.",
    valueType: "currency",
    iconClassName: "text-emerald-600",
    iconBackgroundClassName: "bg-emerald-50",
    iconRingClassName: "ring-emerald-100",
    tooltipClassName: "border-emerald-200 bg-emerald-100 text-emerald-950 before:bg-emerald-100",
  },
  {
    key: "fidelity",
    label: "Points fidélité",
    mobileLabel: "Fidélité",
    path: USER_SPACE_PATHS.fidelity,
    icon: StarIcon,
    description: "Points gagnés grâce aux actions réalisées sur monCVpro.",
    valueType: "points",
    iconClassName: "text-amber-600",
    iconBackgroundClassName: "bg-amber-50",
    iconRingClassName: "ring-amber-200",
    tooltipClassName: "border-amber-200 bg-amber-100 text-amber-950 before:bg-amber-100",
  },
];

const formatNumber = (value: number) => {
  return new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 0,
  }).format(value);
};

const formatValue = (metric: TopbarMetric, value: number) => {
  if (metric.valueType === "points") {
    return `${formatNumber(value)} pts`;
  }

  return metric.suffix ? `${formatNumber(value)} ${metric.suffix}` : formatNumber(value);
};

const isPathActive = (pathname: string, path: string) => {
  return pathname === path || pathname.startsWith(`${path}/`);
};

/**
 * Seuls les téléchargements ouvrent le dialog des offres.
 * Les crédits IA naviguent vers la page dédiée (solde, packs
 * et historique) — le dialog forfait ne s'ouvre que lorsqu'un
 * blocage IA le demande (payload du middleware).
 */
const opensOffersDialog = (key: MetricKey) => {
  return key === "downloads";
};

type MetricCapsuleProps = {
  metric: TopbarMetric;
  value: number;
  note?: string;
  loading: boolean;
  onOpenOffers: () => void;
  mobile?: boolean;
};

const MetricCapsule = ({
  metric,
  value,
  note,
  loading,
  onOpenOffers,
  mobile = false,
}: MetricCapsuleProps) => {
  const { pathname } = useLocation();

  const IconComponent = metric.icon;

  const active = isPathActive(pathname, metric.path);

  const highlighted = useTopbarHighlight((state) => state.metric === metric.key);

  const { format } = useAccountCurrency();

  const formattedValue =
    metric.valueType === "currency" ? format(value) : formatValue(metric, value);

  const description = note ? `${metric.description} ${note}` : metric.description;

  const canOpenOffers = opensOffersDialog(metric.key);

  const handleMetricClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    if (!canOpenOffers) {
      return;
    }

    event.preventDefault();
    onOpenOffers();
  };

  const content = (
    <Link
      to={metric.path}
      onClick={handleMetricClick}
      aria-current={active && !canOpenOffers ? "page" : undefined}
      aria-label={
        canOpenOffers
          ? `${metric.label} : ${formattedValue}.${note ? ` ${note}` : ""} Ouvrir les offres.`
          : `${metric.label} : ${formattedValue}. ${description}`
      }
      className={cn(
        "group block min-w-0 rounded-2xl",
        "transition-transform duration-200",
        "hover:-translate-y-0.5",
        "focus-visible:outline-none focus-visible:ring-2",
        "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
        mobile ? "w-[156px] shrink-0" : "w-full",
      )}
    >
      <span
        className={cn(
          "mb-1.5 block truncate text-center",
          "font-medium leading-none text-indigo-950",
          mobile ? "text-[10px]" : "text-[10px] xl:text-[11px]",
        )}
      >
        {mobile ? metric.mobileLabel : metric.label}
      </span>

      <span className="flex min-w-0 items-center justify-center">
        <span
          className={cn(
            "relative z-10 grid shrink-0 place-items-center",
            "rounded-full border-2 border-white",
            "shadow-[0_4px_14px_rgba(15,23,42,0.11)]",
            "ring-1",
            mobile ? "size-11" : "size-10 xl:size-11",
            metric.iconBackgroundClassName,
            metric.iconRingClassName,
            metric.iconClassName,
            active && !canOpenOffers && "ring-2 ring-indigo-300 ring-offset-1",
            highlighted && "ring-4 ring-emerald-300",
          )}
        >
          {highlighted && (
            <span
              aria-hidden
              className="absolute inset-0 animate-ping rounded-full bg-emerald-300/60"
            />
          )}
          <IconComponent aria-hidden size={mobile ? 22 : 21} weight="duotone" />
        </span>

        <span
          className={cn(
            "-ml-2.5 flex min-w-0 flex-1 items-center",
            "rounded-full border border-slate-200 bg-white",
            "font-bold text-indigo-950",
            "shadow-[0_3px_11px_rgba(15,23,42,0.08)]",
            "transition-all duration-200",
            "group-hover:border-indigo-200",
            "group-hover:shadow-[0_7px_18px_rgba(79,70,229,0.13)]",
            active && !canOpenOffers && "border-indigo-200 bg-indigo-50",
            highlighted && "border-emerald-300 bg-emerald-50",
            mobile ? "h-9 pl-6 pr-2 text-xs" : "h-8 pl-6 pr-2 text-[10px] xl:h-9 xl:text-xs",
          )}
        >
          {loading ? (
            <span className="h-3 w-14 animate-pulse rounded-full bg-slate-200" />
          ) : (
            <>
              <span className="min-w-0 flex-1 truncate">{formattedValue}</span>

              {canOpenOffers && (
                <span
                  aria-hidden
                  className={cn(
                    "ml-1.5 grid shrink-0 place-items-center rounded-full",
                    "bg-indigo-50 text-indigo-700",
                    "ring-1 ring-indigo-100",
                    "transition-colors duration-200",
                    "group-hover:bg-indigo-100",
                    mobile ? "size-5" : "size-[18px]",
                  )}
                >
                  <PlusIcon size={mobile ? 12 : 11} weight="bold" />
                </span>
              )}
            </>
          )}
        </span>
      </span>
    </Link>
  );

  if (mobile) {
    return content;
  }

  return (
    <Tooltip
      side="bottom"
      sideOffset={12}
      content={canOpenOffers ? `${description} Cliquez pour découvrir les offres.` : description}
      className={cn(
        "relative max-w-64 rounded-2xl border px-4 py-3",
        "text-center text-xs font-medium leading-5",
        "shadow-[0_12px_30px_rgba(15,23,42,0.15)]",
        "before:absolute before:-top-1.5 before:left-1/2",
        "before:size-3 before:-translate-x-1/2",
        "before:rotate-45 before:content-['']",
        metric.tooltipClassName,
      )}
    >
      {content}
    </Tooltip>
  );
};

export const UserSpaceTopbar = ({
  planName = "FREE",
  downloads = 0,
  downloadsExpiring = [],
  aiCredits = 0,
  rewards = 0,
  fidelity = 0,
  loading = false,
  refreshing = false,
  onRefresh,
}: UserSpaceTopbarProps) => {
  const { user } = useUser();

  const { open: openOffers } = useDialog("forfait");

  const handleOpenOffers = () => {
    openOffers("create", {
      id: "forfait",
    });
  };

  const metricValues: Record<MetricKey, number> = {
    downloads,
    ai: aiCredits,
    rewards,
    fidelity,
  };

  const downloadsNote =
    downloadsExpiring.length > 0 ? `Dont ${describeCreditExpiry(downloadsExpiring)}.` : undefined;

  return (
    <>
      <header
        className={cn(
          "sticky top-0 z-30 hidden h-[96px]",
          "shrink-0 items-center border-b border-slate-200",
          "bg-white/95 px-4 backdrop-blur-xl lg:flex",
          "xl:px-5 2xl:px-7",
        )}
      >
        <nav
          aria-label="Résumé du compte"
          className={cn("grid min-w-0 flex-1 grid-cols-4", "items-center gap-2 xl:gap-3 2xl:gap-5")}
        >
          {topbarMetrics.map((metric) => (
            <MetricCapsule
              key={metric.key}
              metric={metric}
              value={metricValues[metric.key]}
              note={metric.key === "downloads" ? downloadsNote : undefined}
              loading={loading}
              onOpenOffers={handleOpenOffers}
            />
          ))}
        </nav>

        <div className={cn("ml-4 flex shrink-0 items-center gap-3", "xl:ml-5 xl:gap-4")}>
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing || loading}
            aria-label="Actualiser les soldes"
            title="Actualiser les soldes"
            className={cn(
              "grid size-9 place-items-center rounded-full text-slate-500",
              "border border-transparent bg-white shadow-sm ring-1 ring-slate-200",
              "transition-all duration-200 hover:bg-indigo-50 hover:text-indigo-600 hover:ring-indigo-300",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
              (refreshing || loading) && "cursor-not-allowed opacity-50",
            )}
          >
            <ArrowsClockwise
              size={18}
              weight="bold"
              className={cn((refreshing || loading) && "animate-spin")}
            />
          </button>

          <NotificationBell />

          <div aria-hidden className="h-10 w-px bg-slate-200" />

          <UserOptions>
            <button
              type="button"
              aria-label="Ouvrir les options du compte"
              className={cn(
                "flex items-center gap-2.5 rounded-xl p-1.5",
                "text-left transition-colors hover:bg-slate-50",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-indigo-500",
              )}
            >
              <UserAvatar size={42} />

              <span className="hidden min-w-0 2xl:block">
                <span className="block max-w-32 truncate text-[13px] font-bold text-indigo-950">
                  {user?.name || "Utilisateur"}
                </span>

                <span className="block max-w-32 truncate text-[10px] text-slate-500">
                  Offre {planName}
                </span>
              </span>
            </button>
          </UserOptions>
        </div>
      </header>
    </>
  );
};
